import { useEffect } from 'react';
import { Select, SelectItem, Button } from '@heroui/react';
import { clampPage, pageRange, totalPages as countPages } from '@/lib/pagination';
import { ChevronLeftIcon, ChevronRightIcon } from '@/icons';

interface PaginationProps {
  page: number;
  pageSize: number;
  /** Total rows, or undefined while unknown (loading/error): no clamping until known. */
  total: number | undefined;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
}

/** Server-driven pagination bar: range summary, size selector, prev/next. */
export function Pagination({
  page, pageSize, total, onPageChange, onPageSizeChange, pageSizeOptions = [10, 25, 50, 100],
}: PaginationProps) {
  const known = typeof total === 'number' && Number.isFinite(total);
  const totalPages = known ? countPages(total, pageSize) : Math.max(1, page);
  // Never render an impossible position ("Page 2 / 1", "26-2 of 2"): show the
  // clamped page, and move the caller (and its URL) there once the total is known.
  const current = known ? clampPage(page, total, pageSize) : page;
  const { from, to } = known ? pageRange(current, pageSize, total) : { from: 0, to: 0 };

  useEffect(() => {
    if (known && current !== page) onPageChange(current);
  }, [known, current, page, onPageChange]);

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t border-border px-4 py-3 sm:flex-row">
      <p className="text-sm text-fg-muted">
        {known ? `${from}-${to} of ${total.toLocaleString()}` : '\u2014'}
      </p>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-fg-muted">Rows</span>
          <Select
            aria-label="Rows per page"
            size="sm"
            className="w-20"
            selectedKeys={new Set([String(pageSize)])}
            onSelectionChange={(keys) => {
              const val = Array.from(keys)[0];
              if (val) onPageSizeChange(Number(val));
            }}
          >
            {pageSizeOptions.map((n) => (
              <SelectItem key={String(n)}>{String(n)}</SelectItem>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-1">
          <Button
            isIconOnly size="sm" variant="flat" aria-label="Previous page"
            isDisabled={current <= 1} onPress={() => onPageChange(current - 1)}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
          <span className="min-w-[5rem] text-center text-sm text-fg-muted">
            Page {current} / {totalPages}
          </span>
          <Button
            isIconOnly size="sm" variant="flat" aria-label="Next page"
            isDisabled={!known || current >= totalPages} onPress={() => onPageChange(current + 1)}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
